import React, { useContext, useEffect, useState, useTransition } from 'react'
import DateNTimeComponent from './DateNTimeComponent';
import styled from 'styled-components';
import SelectMeetingType from './SelectMeetingType';
import { Color, Colors } from '../styles/Colors';
import SelectCommittee from './SelectCommittee';
import DateComponent from './DateComponent';
import { AgendaContext } from '../context/AgendaContext';
import { getCommittee, getDocumentData, getRecentlyDeterminedPublications, getProposalForSweTitles } from '../services/api';
import { v4 as uuidv4 } from 'uuid'
import { MediumButton } from '../styles/Buttons';
import LoadingOverlay from './LoadingOverlay';
import Spinner from './Spinner';
import ToggleSection from './ToggleSection';
import SwicthButton from './SwicthButton';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
    border-bottom: 2px solid ${Color(Colors.black)};

    & > h1 {
        padding-bottom: 2rem; 
    }
`;

const Content = styled.div`
    display: flex;
    margin: 1rem 0 1rem 0; 
    min-height: 32px; 
    height: 100%;
    width: 100%;
    
    & > div:first-of-type,
    & > div:last-of-type {
        flex: 1; 
        display: flex;  
        
        & > h4,
        & > p  {
            padding: 0; 
            margin: 0; 
        }
    }
        
    & > div:last-of-type {
        align-items: flex-end;
        flex-direction: column; 
    }
    
`;

const LanguageContainer = styled.div`
    display: flex; 
    justify-content: flex-start;
    align-items: center; 
    padding: 0 0 .5rem 0; 

    & > p {
        padding: 0 .5rem 0 0; 
    }
`;

const Input = styled.input`
    padding: 0.5rem; 
    max-width: 315px;
    margin: 0; 
`;

const Roles = styled.div`
    flex-direction: column; 

    & > p {
        padding: 0 0 0.5rem 0 !important; 

        &:last-of-type {
            padding-bottom: 0 !important;   
        }
    }
`;

const Error = styled.div`
    display: flex;  
    justify-content: flex-end; 
    color: ${Color(Colors.red)}; 
    padding: 0.5rem 0 0 0;
`;

const InformationMsg = styled.div`
    display: flex;  
    justify-content: flex-end; 

    & > p {
        padding: 0.5rem 0 0 0;
    }

`;

const Wrapper = styled.div`
    & > div:first-of-type, 
    & > div:last-of-type {
        margin: 0; 
    }
`;

const Agenda = ({ nextMeetingRef }) => {
    const {
        roles, setRoles, meetingNumber, setMeetingNumber,
        setIecInfoDocs, setIecActionDocs, lastMeeting, setLastMeeting, 
        committeeName, setCorrespondenceDocs, isAgendaContentLoading, setIsAgendaContentLoading, setParticipants,
        isAgendaContentVisible, setIsAgendaContentVisible, nextMeeting, proposalForSweTitles, setProposalForSweTitles, setUpIntlComingMeetings,
        meetingDates, setMeetingDates, setClcActionDocs, setMeetingLink, setMeetingId, setMeetingPassCode, 
        setPreviousProtocol, setSelectedMeetingType, setIecClcWgReports, setClcInfoDocs, setEditableSections, setUpComingSweMeetings, recentlyDeterminedPubl, setRecentlyDeterminedPubl,
        isLanguageEn, setIsLanguageEn, setToggleToc, setParticipantsIsOpened, 
    } = useContext(AgendaContext);
    const { t } = useTranslation(); 
    const [error, setError] = useState('');

    useEffect(() => {
        localStorage.setItem('meeting_number', meetingNumber)
    }, [meetingNumber]);

    const toggleLanguage = (event) => {
        const checked = event.target.checked; 
        setIsLanguageEn(checked); 
        localStorage.setItem('is_language_en', JSON.stringify(checked)); 
    }

    const loading = {
        icon: Spinner,
        iconFill: Color(Colors.blue),
        title: t('loading.title'),
        subTitle: t('loading.subTitle', { committee: committeeName?.label })
    }

    const id = (doc, i, name) => {
        const key = ((name === 'upcoming_intl_meetings') || (name === 'member')) ? `member_id:${doc.name}_${i}` : `${name}_${doc.document}_id`
        let storedId = localStorage.getItem(key)
        if (!storedId) {
            storedId = uuidv4();
            localStorage.setItem(key, storedId)
        }

        return storedId
    }

    const fetchAgenda = async () => {
        setIsAgendaContentLoading(true);
        const newError = {};

        if (committeeName === '') {
            newError.committeeName = t('committeeSelection.errorHandling.committe');
        }
        
        if (lastMeeting === '') {
            newError.lastMeeting = t('committeeSelection.errorHandling.lastMeeting');
        } 
        
        if (nextMeeting.length === 0) {
            newError.nextMeeting = t('committeeSelection.errorHandling.nextMeeting');
        }

        if (Object.keys(newError).length > 0) {
            setError(newError);
            setIsAgendaContentLoading(false);
            return;
        }

        try {
            const selectedNextMeeting = nextMeeting[0];

            if (committeeName !== null && lastMeeting !== '' && selectedNextMeeting !== undefined) {
                const selectedCommittee = committeeName.value;
                const storedCommittee = JSON.parse(localStorage.getItem('committee_name')) 
                
                if (storedCommittee) {
                    if ((storedCommittee && (storedCommittee.value !== selectedCommittee))) {
                        localStorage.removeItem('selected_meeting_type');
                        localStorage.removeItem('meeting_number');
                        localStorage.removeItem('meeting_link');
                        localStorage.removeItem('meeting_id');
                        localStorage.removeItem('meeting_passcode');
                        localStorage.removeItem('previous_protocol');
                        localStorage.removeItem('participant');
                        localStorage.removeItem('correspondencen_docs');
                        localStorage.removeItem('action_docs');
                        localStorage.removeItem('clc_action_docs');
                        localStorage.removeItem('upcoming_intl_meetings');
                        localStorage.removeItem('upcoming_swe_meetings');
                        localStorage.removeItem('information_docs');
                        localStorage.removeItem('clc_info_docs');
                        localStorage.removeItem('reports_IEC_CLC');
                        localStorage.removeItem('other_info_and_activities');
                        localStorage.removeItem('any_other_business');
                        localStorage.removeItem('is_language_en');
                        localStorage.removeItem('toggle_table_of_contents');
                        localStorage.removeItem('participants_is_open');
                        setIsLanguageEn(false); 
                        setToggleToc(true); 
                        setParticipantsIsOpened(false); 
                        setSelectedMeetingType(''); 
                        setMeetingNumber('');
                        setMeetingLink(''); 
                        setMeetingId('');
                        setMeetingPassCode('');
                        setPreviousProtocol('');
                        setParticipants([]); 
                        setIecActionDocs([]);
                        setCorrespondenceDocs([]);
                        setClcActionDocs([]);
                        setUpIntlComingMeetings([]);
                        setUpComingSweMeetings([]);
                        setIecInfoDocs([]);
                        setClcInfoDocs([]); 
                        setIecClcWgReports([]); 
                        setEditableSections({
                            otherInfoAndActivities: [],
                            anyOtherBusiness: [],
                            recurringTopics: [], 
                        }); 

                        // Remove dubletts - if user edit a item and re-select the same committee again  
                        for (let i = 0; i < localStorage.length; i++) {
                            const key = localStorage.key(i); 
                            if (key.startsWith('action_doc_') || key.startsWith('correspondence_doc_') || key.startsWith('information_doc_') || key.startsWith('member_id:')) {
                                localStorage.removeItem(key)
                                i--; 
                            }
                        }
                    }
                }

                const committeeInfomation = await getCommittee(selectedCommittee, lastMeeting);
                const documentData = await getDocumentData(selectedCommittee, lastMeeting, selectedNextMeeting);
                const newTitles = await getProposalForSweTitles(selectedCommittee); 
                const recentPublications = await getRecentlyDeterminedPublications(selectedCommittee); 

                if (committeeInfomation?.members) {
                    const participant = committeeInfomation.members.map((participant, i) => ({
                        ...participant, 
                        id: id(participant, i, 'member'),
                    }));
                    setParticipants(participant); 
                }
                
                if (committeeInfomation?.meetings) {
                    setMeetingDates(committeeInfomation.meetings);
                }

                if (committeeInfomation?.roles) {
                    setRoles(committeeInfomation.roles);
                }

                if (documentData?.information_docs) {
                    const iecInfoDocs = documentData.information_docs.map(doc => ({
                        ...doc,
                        id: id(doc, 'information_doc'),
                    }));
                    setIecInfoDocs(iecInfoDocs);
                }

                if (documentData?.action_docs) {
                    const iecActionDocs = documentData.action_docs.map(doc => ({
                        ...doc,
                        id: id(doc, 'action_doc'),
                        action: '',
                    }));
                    setIecActionDocs(iecActionDocs);
                }

                if (documentData?.correspondence_docs) {
                    const correspondenceDocs = documentData.correspondence_docs.map(doc => {
                        return {
                            ...doc,
                            id: id(doc, 'correspondence_doc'),
                        };
                    });
                    setCorrespondenceDocs(correspondenceDocs);
                }

                if (documentData?.upcoming_meetings) {
                    const upcomingIntlMeetings = documentData.upcoming_meetings.map(item => {
                        return {
                            ...item, 
                            id: id(item, 'upcoming_intl_meetings'),
                        }
                    })
                    setUpIntlComingMeetings(upcomingIntlMeetings)

                }

                if (proposalForSweTitles) {
                    setProposalForSweTitles(newTitles); 
                }

                if (recentlyDeterminedPubl) {
                    setRecentlyDeterminedPubl(recentPublications); 
                }

                localStorage.setItem('committee_name', JSON.stringify(committeeName));
                setIsAgendaContentVisible(true);
                setError({})
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsAgendaContentLoading(false);
        }
    };

    return (
        <Container>
            <LanguageContainer>
                <p>{`${t('switchLang')}:`}</p>
                <SwicthButton 
                    checked={isLanguageEn}
                    onChange={toggleLanguage}
                />
            </LanguageContainer>
            <h1>{t('toolName')}</h1>
            {isAgendaContentLoading ? (
                <>
                    {<LoadingOverlay {...loading} />}
                </>
            ) : (
                <div>
                    <div>
                        <h4 style={{ padding: '0 0 1rem 0' }}>{t('committeeSelection.committee')}</h4>
                        <SelectCommittee setError={setError} />
                        {Object.keys(error).length > 0 && (
                            <Error>
                                <p>{error.committeeName}</p>
                            </Error>
                        )}
                    </div>
                    <Content>
                        <div>
                            <div>
                                <h4>{t('committeeSelection.lastMeeting')}</h4>
                            </div>
                        </div>
                        <div>
                            <div>
                                <DateComponent 
                                    usersDate={lastMeeting}
                                    setUsersDate={setLastMeeting}
                                    setError={setError} 
                                    tool='agendaGenerator'
                                    usersDateKey='last_meeting'
                                />
                                {Object.keys(error).length > 0 && (
                                    <Error>
                                        <p>{error.lastMeeting}</p>
                                    </Error>
                                )}
                                {(meetingDates && meetingDates['last_meeting']) && (
                                    <InformationMsg>
                                        <p>{`${t('committeeSelection.meetingRetrievedFrmLime')}: ${new Date(meetingDates['last_meeting']).toLocaleDateString('sv-SE')}`}</p>
                                    </InformationMsg>
                                )}

                            </div>
                        </div>
                    </Content>
                    <Content>
                        <div>
                            <div style={{ display: 'block' }}>
                                <h4>{t('committeeSelection.nextMeeting')}</h4>

                            </div>
                        </div>
                        <div ref={nextMeetingRef}>
                            <div>
                                <div>
                                    <DateNTimeComponent setError={setError} />
                                    {Object.keys(error).length > 0 && (
                                        <Error>
                                            <p>{error.nextMeeting}</p>
                                        </Error>
                                    )}
                                    {(meetingDates && meetingDates['next_meeting']) && (
                                        <InformationMsg>
                                            <p>{`${t('committeeSelection.meetingRetrievedFrmLime')}: ${new Date(meetingDates['next_meeting']).toLocaleDateString('sv-SE')}`}</p>
                                        </InformationMsg>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Content>
                    <MediumButton onClick={fetchAgenda} style={{ margin: '0 0 2rem 0' }}>{t('committeeSelection.buttonLabel')}</MediumButton>
                </div>
            )}
            {isAgendaContentVisible && !isAgendaContentLoading && (
                <ToggleSection title={t('meetingDetails.sectionLabel')}>
                    <Wrapper>
                        <Content>
                            <div>
                                <h4>{t('meetingDetails.location.name')}</h4>
                            </div>
                            <div>
                                <div>
                                    <SelectMeetingType />
                                </div>
                            </div>
                        </Content>
                        <Content>
                            <div><h4>{t('meetingDetails.sekContact')}</h4></div>
                            <Roles>
                                {roles.sek_contact.length > 0 ? (
                                    <p>{roles.sek_contact[0]}</p>
                                ) : (
                                    <p>{t('meetingDetails.vacant')}</p>
                                )}
                            </Roles>
                        </Content>
                        <Content>
                            <div><h4>{t('meetingDetails.contactPerson')}</h4></div>
                            <Roles>
                                {roles.contact.length > 0 ? (
                                    roles.contact.map((role, index) => (
                                        <p key={index}>{role}</p>
                                    ))
                                ) : (
                                    <p>{t('meetingDetails.vacant')}</p>
                                )}
                            </Roles>
                        </Content>
                        <Content>
                            <div><h4>{t('meetingDetails.chairman')}</h4></div>
                            <Roles>
                                {roles.chairman.length > 0 ? (
                                    roles.chairman.map((role, index) => (
                                        <p key={index}>{role}</p>
                                    ))
                                ) : (
                                    <p>{t('meetingDetails.vacant')}</p>
                                )}
                            </Roles>
                        </Content>
                        <Content>
                            <div><h4>{t('meetingDetails.secretary')}</h4></div>
                            <Roles>
                                {roles.secretary.length > 0 ? (
                                    roles.secretary.map((role, index) => (
                                        <p key={index}>{role}</p>
                                    ))
                                ) : (
                                    <p>{t('meetingDetails.vacant')}</p>
                                )}
                            </Roles>
                        </Content>
                        <Content>
                            <div><h4>{t('meetingDetails.meetingNumber.name')}</h4></div>
                            <div>
                                <Input 
                                    type='number'
                                    min='1'
                                    value={meetingNumber || ''}
                                    onChange={(event) => setMeetingNumber(parseInt(event.target.value, 10) || '')}
                                    placeholder={t('meetingDetails.meetingNumber.label')}
                                />
                            </div>
                        </Content>
                    </Wrapper>
                </ToggleSection>
            )}
        </Container>
    )
}

export default Agenda